// src/features/coupon/pages/UserCouponPage.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  ArrowLeft,
  Gift,
  RefreshCw,
  Ticket,
  X,
} from 'lucide-react';

import {
  useNavigate,
} from 'react-router-dom';

import CouponCard
  from '../components/CouponCard';

import ScratchRewardModal
  from '../components/ScratchRewardModal';

import {
  userCouponService,
} from '../services/userCouponService';

import {
  storeService,
} from '../../store/services/storeService';

import type {
  CouponRewardType,
  UserCoupon,
  UserCouponStatus,
} from '../types/coupon.types';


type CouponFilter =
  | 'ALL'
  | UserCouponStatus;


const FILTERS: {
  value: CouponFilter;
  label: string;
}[] = [
  {
    value: 'ALL',
    label: '전체',
  },
  {
    value: 'AVAILABLE',
    label: '사용 가능',
  },
  {
    value: 'USED',
    label: '사용 완료',
  },
  {
    value: 'EXPIRED',
    label: '만료',
  },
];


const formatNumber = (
  value: number | null,
): string => {

  if (
    value == null
  ) {
    return '-';
  }

  return value.toLocaleString(
    'ko-KR',
  );
};


const getRewardResultLabel = (
  rewardType:
    CouponRewardType | null,
  rewardAmount:
    number | null,
): string => {

  if (!rewardType) {
    return '보상 지급 완료';
  }


  switch (
    rewardType
  ) {

    case 'POINT':
      return `${formatNumber(
        rewardAmount,
      )}P를 받았습니다.`;

    case 'STORE_POINT':
      return `${formatNumber(
        rewardAmount,
      )} 매장포인트를 받았습니다.`;

    case 'FREE_GAME':
      return `무료게임 ${formatNumber(
        rewardAmount,
      )}회`;

    case 'SATELLITE':
      return `새틀라이트 ${formatNumber(
        rewardAmount,
      )}회`;

    case 'SEAT':
      return `시트권 ${formatNumber(
        rewardAmount,
      )}매`;

    case 'ETC':
      return '쿠폰 보상이 지급되었습니다.';

    default:
      return '보상 지급 완료';
  }
};


export default function UserCouponPage():
  React.JSX.Element {

  /**
   * =========================================================
   * Navigation
   * =========================================================
   */
  const navigate =
    useNavigate();


  const [
    coupons,
    setCoupons,
  ] =
    useState<
      UserCoupon[]
    >([]);


  const [
    storeNameMap,
    setStoreNameMap,
  ] =
    useState<
      Record<number, string>
    >({});


  const [
    filter,
    setFilter,
  ] =
    useState<CouponFilter>(
      'AVAILABLE',
    );


  const [
    loading,
    setLoading,
  ] =
    useState(true);


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState('');


  const [
    usingCouponId,
    setUsingCouponId,
  ] =
    useState<
      number | null
    >(null);


  const [
    rewardResult,
    setRewardResult,
  ] =
    useState<
      UserCoupon | null
    >(null);


  /**
   * =========================================================
   * 쿠폰 목록 조회
   * =========================================================
   */
  const loadCoupons =
    useCallback(
      async (): Promise<void> => {

        try {

          setLoading(
            true,
          );

          setErrorMessage(
            '',
          );


          const data =
            await userCouponService
              .getMyCoupons();


          setCoupons(
            data,
          );

        } catch (
          error
        ) {

          console.error(
            '쿠폰 조회 실패:',
            error,
          );


          setErrorMessage(
            '쿠폰을 불러오지 못했습니다.',
          );

        } finally {

          setLoading(
            false,
          );

        }
      },
      [],
    );


  /**
   * =========================================================
   * 매장 목록 조회
   * =========================================================
   */
  const loadStores =
    useCallback(
      async (): Promise<void> => {

        try {

          const stores =
            await storeService
              .getStores();


          const nextMap:
            Record<number, string> =
              {};


          stores.forEach(
            (store) => {

              nextMap[
                store.storeId
              ] =
                store.storeName;

            },
          );


          setStoreNameMap(
            nextMap,
          );

        } catch (
          error
        ) {

          console.error(
            '매장 목록 조회 실패:',
            error,
          );

        }
      },
      [],
    );


  /**
   * =========================================================
   * 최초 로딩
   * =========================================================
   */
  useEffect(() => {

    void Promise.all([
      loadCoupons(),
      loadStores(),
    ]);

  }, [
    loadCoupons,
    loadStores,
  ]);


  /**
   * =========================================================
   * 쿠폰 사용
   *
   * FIXED
   * -> 서버에서 사용
   * -> 일반 결과 모달
   *
   * RANDOM
   * -> 서버에서 추첨 + 보상 지급
   * -> ScratchRewardModal
   *
   * 중요:
   * 랜덤 결과 자체는 서버에서 이미 결정됩니다.
   * Scratch UI는 결과를 보여주는 연출입니다.
   * =========================================================
   */
  const handleUseCoupon =
    async (
      coupon: UserCoupon,
    ): Promise<void> => {

      if (
        usingCouponId != null
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          coupon.rewardMode ===
            'RANDOM'
            ? '랜덤 쿠폰을 사용하시겠습니까? 사용 즉시 보상이 결정됩니다.'
            : '이 쿠폰을 사용하시겠습니까?',
        );


      if (!confirmed) {
        return;
      }


      try {

        setUsingCouponId(
          coupon.userCouponId,
        );


        const updated =
          await userCouponService
            .useCoupon(
              coupon.userCouponId,
            );


        /**
         * 현재 목록 즉시 반영
         */
        setCoupons(
          (previous) =>
            previous.map(
              (item) =>
                item.userCouponId ===
                  updated.userCouponId
                  ? updated
                  : item,
            ),
        );


        /**
         * 결과 표시
         *
         * RANDOM이면
         * ScratchRewardModal
         *
         * FIXED이면
         * 일반 완료 모달
         */
        setRewardResult(
          updated,
        );

      } catch (
        error
      ) {

        console.error(
          '쿠폰 사용 실패:',
          error,
        );


        window.alert(
          '쿠폰 사용에 실패했습니다.',
        );

      } finally {

        setUsingCouponId(
          null,
        );

      }
    };


  /**
   * =========================================================
   * 현재 필터 적용
   * =========================================================
   */
  const filteredCoupons =
    useMemo(
      () => {

        if (
          filter === 'ALL'
        ) {
          return coupons;
        }


        return coupons.filter(
          (coupon) =>
            coupon.status ===
            filter,
        );
      },
      [
        coupons,
        filter,
      ],
    );


  /**
   * =========================================================
   * 사용 가능 쿠폰 수
   * =========================================================
   */
  const availableCount =
    useMemo(
      () =>
        coupons.filter(
          (coupon) =>
            coupon.status ===
            'AVAILABLE',
        ).length,
      [
        coupons,
      ],
    );


  /**
   * =========================================================
   * 결과로 표시할 실제 보상 타입
   *
   * RANDOM:
   * selectedRewardType
   *
   * FIXED:
   * rewardType
   * =========================================================
   */
  const resultRewardType =
    rewardResult == null
      ? null
      : rewardResult.rewardMode ===
          'RANDOM'
        ? rewardResult
            .selectedRewardType
        : rewardResult
            .rewardType;


  /**
   * =========================================================
   * 결과로 표시할 실제 보상 수량
   * =========================================================
   */
  const resultRewardAmount =
    rewardResult == null
      ? null
      : rewardResult.rewardMode ===
          'RANDOM'
        ? rewardResult
            .selectedRewardAmount
        : rewardResult
            .rewardAmount;


  /**
   * =========================================================
   * 결과 문구
   * =========================================================
   */
  const resultRewardText =
    rewardResult == null
      ? ''
      : getRewardResultLabel(
          resultRewardType,
          resultRewardAmount,
        );


  /**
   * =========================================================
   * 결과 보상 매장
   *
   * RANDOM:
   * selectedRewardStoreId
   *
   * FIXED:
   * storeId
   * =========================================================
   */
  const rewardStoreId =
    rewardResult == null
      ? null
      : rewardResult.rewardMode ===
          'RANDOM'
        ? rewardResult
            .selectedRewardStoreId
        : rewardResult
            .storeId;


  const rewardStoreName =
    rewardStoreId == null
      ? undefined
      : storeNameMap[
          rewardStoreId
        ];


  return (
    <main
      className="
        min-h-screen
        bg-gray-50
        px-4
        py-5
        sm:px-6
        sm:py-7
        lg:px-8
      "
    >

      <div
        className="
          mx-auto
          w-full
          max-w-5xl
        "
      >

        {/*
         * =====================================================
         * Back
         * =====================================================
         */}
        <button
          type="button"
          onClick={() =>
            navigate(-1)
          }
          className="
            mb-3
            inline-flex
            h-9
            items-center
            gap-1.5
            rounded-lg
            px-2
            text-sm
            font-semibold
            text-gray-600
            transition
            hover:bg-gray-100
            hover:text-gray-900
          "
        >
          <ArrowLeft
            size={18}
            strokeWidth={2}
          />

          뒤로가기
        </button>


        {/*
         * =====================================================
         * Header
         * =====================================================
         */}
        <header
          className="
            mb-5
            flex
            flex-col
            gap-3
            sm:mb-6
            sm:flex-row
            sm:items-end
            sm:justify-between
          "
        >

          <div>

            <p
              className="
                text-xs
                font-bold
                tracking-[0.18em]
                text-main
              "
            >
              MY COUPON
            </p>


            <h1
              className="
                mt-1
                text-2xl
                font-bold
                text-gray-900
                sm:text-3xl
              "
            >
              내 쿠폰함
            </h1>


            <p
              className="
                mt-1
                text-sm
                text-gray-500
              "
            >
              사용 가능한 쿠폰이{' '}

              <strong
                className="text-main"
              >
                {availableCount}장
              </strong>{' '}

              있습니다.
            </p>

          </div>


          <button
            type="button"
            disabled={
              loading
            }
            onClick={() =>
              void Promise.all([
                loadCoupons(),
                loadStores(),
              ])
            }
            className="
              inline-flex
              min-h-10
              items-center
              justify-center
              gap-2
              self-start
              rounded-lg
              border
              border-gray-200
              bg-white
              px-3
              text-sm
              font-semibold
              text-gray-600
              transition
              hover:bg-gray-50
              disabled:opacity-50
            "
          >
            <RefreshCw
              size={16}
              className={
                loading
                  ? 'animate-spin'
                  : ''
              }
            />

            새로고침
          </button>

        </header>


        {/*
         * =====================================================
         * Filter
         * =====================================================
         */}
        <section
          className="
            mb-5
            overflow-x-auto
            pb-1
          "
        >

          <div
            className="
              flex
              min-w-max
              gap-2
            "
          >

            {FILTERS.map(
              (item) => {

                const selected =
                  filter ===
                  item.value;


                return (
                  <button
                    key={
                      item.value
                    }
                    type="button"
                    onClick={() =>
                      setFilter(
                        item.value,
                      )
                    }
                    className={`
                      rounded-full
                      border
                      px-4
                      py-2
                      text-sm
                      font-semibold
                      transition

                      ${
                        selected
                          ? `
                            border-main
                            bg-main
                            text-white
                          `
                          : `
                            border-gray-200
                            bg-white
                            text-gray-600
                            hover:border-main
                            hover:text-main
                          `
                      }
                    `}
                  >
                    {item.label}
                  </button>
                );
              },
            )}

          </div>

        </section>


        {/*
         * =====================================================
         * Content
         * =====================================================
         */}
        {loading ? (

          <div
            className="
              flex
              min-h-[300px]
              items-center
              justify-center
            "
          >
            <span
              className="
                h-9
                w-9
                animate-spin
                rounded-full
                border-4
                border-gray-200
                border-t-main
              "
            />
          </div>

        ) : errorMessage ? (

          <div
            className="
              rounded-2xl
              border
              border-red-100
              bg-white
              px-5
              py-10
              text-center
            "
          >

            <p
              className="
                text-sm
                text-red-500
              "
            >
              {errorMessage}
            </p>


            <button
              type="button"
              onClick={() =>
                void loadCoupons()
              }
              className="
                mt-4
                rounded-lg
                bg-main
                px-4
                py-2
                text-sm
                font-bold
                text-white
              "
            >
              다시 시도
            </button>

          </div>

        ) : filteredCoupons.length ===
          0 ? (

          <div
            className="
              rounded-2xl
              border
              border-gray-200
              bg-white
              px-5
              py-16
              text-center
            "
          >

            <Ticket
              size={36}
              className="
                mx-auto
                text-gray-300
              "
            />


            <p
              className="
                mt-3
                font-semibold
                text-gray-700
              "
            >
              표시할 쿠폰이 없습니다.
            </p>


            <p
              className="
                mt-1
                text-sm
                text-gray-400
              "
            >
              새로운 쿠폰을 받으면 이곳에 표시됩니다.
            </p>

          </div>

        ) : (

          <section
            className="
              grid
              grid-cols-1
              gap-4
              md:grid-cols-2
            "
          >

            {filteredCoupons.map(
              (coupon) => (

                <CouponCard
                  key={
                    coupon.userCouponId
                  }
                  coupon={
                    coupon
                  }
                  storeName={
                    coupon.storeId != null
                      ? storeNameMap[
                          coupon.storeId
                        ]
                      : undefined
                  }
                  using={
                    usingCouponId ===
                    coupon.userCouponId
                  }
                  onUse={
                    handleUseCoupon
                  }
                />

              ),
            )}

          </section>

        )}

      </div>


      {/*
       * =========================================================
       * RANDOM 결과
       *
       * 스크래치 복권 모달
       * =========================================================
       */}
      {rewardResult &&
        rewardResult.rewardMode ===
          'RANDOM' && (

        <ScratchRewardModal
          couponName={
            rewardResult.couponName
          }
          rewardText={
            resultRewardText
          }
          rewardAmount={
            resultRewardAmount
          }
          storeName={
            rewardStoreName
          }
          onClose={() =>
            setRewardResult(
              null,
            )
          }
        />

      )}


      {/*
       * =========================================================
       * FIXED 결과
       *
       * 일반 사용 완료 모달
       * =========================================================
       */}
      {rewardResult &&
        rewardResult.rewardMode ===
          'FIXED' && (

        <div
          className="
            fixed
            inset-0
            z-50
            flex
            items-center
            justify-center
            bg-black/50
            px-4
          "
          onClick={() =>
            setRewardResult(
              null,
            )
          }
        >

          <div
            role="dialog"
            aria-modal="true"
            onClick={(
              event,
            ) =>
              event.stopPropagation()
            }
            className="
              relative
              w-full
              max-w-sm
              rounded-3xl
              bg-white
              px-6
              py-7
              text-center
              shadow-2xl
            "
          >

            <button
              type="button"
              aria-label="닫기"
              onClick={() =>
                setRewardResult(
                  null,
                )
              }
              className="
                absolute
                right-4
                top-4
                flex
                h-8
                w-8
                items-center
                justify-center
                rounded-full
                text-gray-400
                transition
                hover:bg-gray-100
                hover:text-gray-700
              "
            >
              <X
                size={19}
              />
            </button>


            <div
              className="
                mx-auto
                flex
                h-16
                w-16
                items-center
                justify-center
                rounded-full
                bg-main/10
                text-main
              "
            >
              <Gift
                size={31}
              />
            </div>


            <p
              className="
                mt-5
                text-xs
                font-bold
                tracking-[0.15em]
                text-main
              "
            >
              REWARD
            </p>


            <h2
              className="
                mt-1
                text-2xl
                font-bold
                text-gray-900
              "
            >
              쿠폰 사용 완료
            </h2>


            <p
              className="
                mt-3
                text-base
                font-semibold
                text-gray-700
              "
            >
              {resultRewardText}
            </p>


            {rewardStoreId != null && (
              <p
                className="
                  mt-2
                  text-sm
                  text-gray-500
                "
              >
                {rewardStoreName ??
                  `매장 #${rewardStoreId}`}
              </p>
            )}


            <button
              type="button"
              onClick={() =>
                setRewardResult(
                  null,
                )
              }
              className="
                mt-6
                min-h-11
                w-full
                rounded-xl
                bg-main
                px-4
                text-sm
                font-bold
                text-white
                transition
                hover:opacity-90
              "
            >
              확인
            </button>

          </div>

        </div>

      )}

    </main>
  );
}